<template>
  <v-container>
    <h1 class="text-center">📜 Regelwerk</h1>

    <v-card outlined class="pa-4 mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold pl-0 pt-0">Grunddaten</v-card-title>
      <v-row>
        <v-col cols="12" md="6">
          <v-select
            :items="channels"
            item-text="name"
            item-value="id"
            label="Kanal für die Veröffentlichung"
            prepend-icon="mdi-pound"
            outlined
            v-model="channelId"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            :items="roles"
            item-text="name"
            item-value="id"
            label="Rolle bei Bestätigung"
            prepend-icon="mdi-account-check"
            outlined
            clearable
            v-model="confirmRoleId"
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field v-model="title" label="Titel" prepend-icon="mdi-format-title" outlined></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="confirmEmoji"
            label="Bestätigungs-Emoji"
            prepend-icon="mdi-emoticon-outline"
            outlined
            hint="Emoji, mit dem User reagieren müssen, um die Regeln zu bestätigen"
            persistent-hint
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-switch
            v-model="removeRoleOnUnreact"
            label="Rolle wieder entziehen, wenn die Reaktion entfernt wird"
            hint="Aus: Die Rolle bleibt bestehen, auch wenn jemand die Reaktion später wieder entfernt."
            persistent-hint
            dense
          ></v-switch>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="pa-4 mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold pl-0 pt-0">📝 Regeltext</v-card-title>
      <v-alert type="info" text dense border="left" class="mb-4">
        Klicke auf eine Rolle, um sie als "@Rollenname" an der Cursor-Position in den Regeltext einzufügen.
        Beim Veröffentlichen wird "@Rollenname" automatisch in eine klickbare Discord-Rollen-Erwähnung
        umgewandelt (ohne die Rolle zu pingen). Discord-Markdown (z.B. **fett**, *kursiv*, Zeilenumbrüche)
        wird ebenfalls unterstützt.
      </v-alert>
      <div class="mb-3">
        <v-chip
          v-for="role in roles"
          :key="role.id"
          class="mr-2 mb-2"
          small
          outlined
          @click="insertRoleMention(role)"
        >
          @{{ role.name }}
        </v-chip>
      </div>
      <v-textarea
        ref="rulesTextarea"
        v-model="rulesText"
        label="Regeltext"
        prepend-icon="mdi-text-long"
        outlined
        rows="10"
      ></v-textarea>
    </v-card>

    <v-card outlined class="pa-4 mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold pl-0 pt-0">🖼️ Logo</v-card-title>
      <v-file-input
        v-model="logoFile"
        label="Logo auswählen"
        prepend-icon="mdi-image"
        accept="image/*"
        outlined
        truncate-length="20"
        hint="Erscheint als kleines Logo oben rechts in der Regel-Nachricht"
        persistent-hint
      ></v-file-input>
      <div v-if="existingLogoPath" class="d-flex flex-wrap align-center mt-3">
        <v-img
          :src="logoPreviewUrl"
          max-width="120"
          max-height="120"
          class="rounded-lg elevation-2"
        ></v-img>
        <v-btn text small color="red" class="ml-3" @click="removeLogo">
          <v-icon left small>mdi-delete</v-icon>
          Entfernen
        </v-btn>
      </div>
    </v-card>

    <v-row>
      <v-col cols="12" class="text-center">
        <v-btn
          color="primary"
          large
          :block="$vuetify.breakpoint.smAndDown"
          :loading="publishing"
          @click="publish"
        >
          <v-icon left>mdi-send</v-icon>
          Veröffentlichen
        </v-btn>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="publishResult.show"
      timeout="5000"
      :color="publishResult.success ? 'green' : 'red'"
    >
      {{ publishResult.message }}
    </v-snackbar>
  </v-container>
</template>

<script>

import BackendService from '@/services/BackendService';

export default {
  data() {
    return {
      channelId: null,
      confirmRoleId: null,
      title: '',
      confirmEmoji: '✅',
      removeRoleOnUnreact: true,
      rulesText: '',
      channels: [],
      roles: [],
      logoFile: null,
      existingLogoPath: null,
      removeLogoFlag: false,
      publishing: false,
      publishResult: {
        show: false,
        success: false,
        message: '',
      },
    };
  },
  computed: {
    logoPreviewUrl() {
      return `${process.env.VUE_APP_BACKENDSERVICE_BASEURL}img/${this.existingLogoPath}`;
    },
  },
  watch: {
    logoFile(file) {
      if (file) this.removeLogoFlag = false;
    },
  },
  mounted() {
    BackendService.loadRulesSettings().then((data) => {
      const s = data.data.settings;
      this.channelId = s.channel_id;
      this.confirmRoleId = s.confirm_role_id;
      this.title = s.title;
      this.confirmEmoji = s.confirm_emoji || '✅';
      this.removeRoleOnUnreact = Number(s.remove_role_on_unreact) !== 0;
      this.rulesText = s.rules_text || '';
      this.existingLogoPath = s.logo_path;
      this.channels = data.data.channels;
      this.roles = data.data.roles;
    });
  },
  methods: {
    insertRoleMention(role) {
      const mention = `@${role.name}`;
      const textarea = this.$refs.rulesTextarea.$refs.input;
      const start = textarea ? textarea.selectionStart : this.rulesText.length;
      const end = textarea ? textarea.selectionEnd : this.rulesText.length;
      this.rulesText = this.rulesText.slice(0, start) + mention + this.rulesText.slice(end);
      this.$nextTick(() => {
        if (textarea) {
          const cursor = start + mention.length;
          textarea.focus();
          textarea.setSelectionRange(cursor, cursor);
        }
      });
    },
    removeLogo() {
      this.logoFile = null;
      this.existingLogoPath = null;
      this.removeLogoFlag = true;
    },
    publish() {
      this.publishing = true;
      BackendService.saveRulesSettings({
        channel_id: this.channelId,
        confirm_role_id: this.confirmRoleId,
        title: this.title,
        confirm_emoji: this.confirmEmoji,
        remove_role_on_unreact: this.removeRoleOnUnreact ? '1' : '0',
        rules_text: this.rulesText,
        logo: this.logoFile || undefined,
        remove_logo: this.removeLogoFlag ? '1' : '0',
      }).then((data) => {
        this.existingLogoPath = data.data.logoPath;
        return BackendService.publishRules();
      }).then((data) => {
        this.publishResult = {
          show: true,
          success: data.data.success,
          message: data.data.success
            ? 'Regelwerk wurde veröffentlicht!'
            : `Fehlgeschlagen: ${data.data.error}`,
        };
      }).catch((error) => {
        const message = error.response && error.response.data === 'streamer_not_connected_with_dc_bot'
          ? 'Streamer benutzt aktuell den DC-Bot nicht!'
          : 'Fehlgeschlagen: Verbindung zum Server nicht möglich.';
        this.publishResult = { show: true, success: false, message };
      })
        .finally(() => {
          this.publishing = false;
        });
    },
  },
};
</script>
